import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { getCurrentUser } from "../services/authService";
import { fetchBranches } from "../services/branchService";
import { hasFeature } from "../services/features";
import { fetchProduct, syncProductBranches } from "../services/productService";

/**
 * Which branches sell one product — the products twin of ServiceBranchesModal,
 * with the same look. Each row: the branch, its currency (read-only, always the
 * branch's), a price, and stock.
 *
 * A branch ticked for the first time needs a price and an opening stock; the
 * API records that stock as an "opening stock" movement. A branch already
 * selling the product shows its stock read-only with a link to the adjustment
 * flow — only the price is editable here. Unticking is refused while the
 * branch still has stock, here and on the server.
 *
 * Each branch may also set its own low-stock threshold. Blank means the
 * product's default (shown as the placeholder); stock *below* the one in force
 * marks the branch low and sends one alert to the owner and branch manager.
 */
export default function ProductBranchesModal({ product, open, onClose, onSaved }) {
  const { t } = useTranslation();
  // Without the plan's inventory a branch takes only a price: no opening
  // stock, threshold or stock check (the API ignores them).
  const tracksStock = hasFeature(getCurrentUser(), "inventory");
  const [branches, setBranches] = useState([]);
  const [rows, setRows] = useState({});
  const [basePrice, setBasePrice] = useState("");
  const [defaultThreshold, setDefaultThreshold] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !product) return;

    setLoading(true);
    setErrors({});
    setBanner("");

    // Assignments come from the product itself, not from the list row.
    let cancelled = false;

    Promise.all([fetchBranches(1, 100), fetchProduct(product.id)])
      .then(([{ branches: list }, full]) => {
        if (cancelled) return;

        setBranches(list);
        setBasePrice(full.base_price ?? full.price ?? "");
        setDefaultThreshold(full.low_stock_threshold ?? null);

        const current = {};
        (full.branches ?? []).forEach((entry) => {
          current[entry.branch_id] = {
            offered: true,
            assigned: true,
            price: entry.price ?? "",
            stock: entry.stock_quantity ?? 0,
            openingStock: "",
            threshold: entry.low_stock_threshold == null ? "" : String(entry.low_stock_threshold),
          };
        });
        setRows(current);
      })
      .catch((err) => {
        if (!cancelled) setBanner(apiErrorMessage(err, t("products.branches.loadFailed")));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, product?.id, t]);

  const row = (branchId) =>
    rows[branchId] ?? { offered: false, assigned: false, price: "", stock: 0, openingStock: "", threshold: "" };

  const update = (branchId, changes) =>
    setRows((current) => ({ ...current, [branchId]: { ...row(branchId), ...changes } }));

  const toggle = (branchId, checked) => {
    const current = row(branchId);
    // A newly ticked branch starts from the product's base price.
    const price = checked && current.price === "" ? String(basePrice) : current.price;
    update(branchId, { offered: checked, price });
    setErrors((e) => ({ ...e, [branchId]: undefined }));
  };

  /** Client rules, mirroring the API's, keyed by branch id. */
  const validate = () => {
    const found = {};

    branches.forEach((branch) => {
      const current = row(branch.id);

      if (!current.offered) {
        if (tracksStock && current.assigned && current.stock > 0) {
          found[branch.id] = t("products.branches.errors.hasStock", { count: current.stock });
        }
        return;
      }

      if (current.price === "" || Number.isNaN(Number(current.price)) || Number(current.price) < 0) {
        found[branch.id] = t("products.branches.errors.price");
      } else if (
        tracksStock &&
        !current.assigned &&
        (current.openingStock === "" ||
          !Number.isInteger(Number(current.openingStock)) ||
          Number(current.openingStock) < 0)
      ) {
        found[branch.id] = t("products.branches.errors.openingStock");
      } else if (
        current.threshold !== "" &&
        (!Number.isInteger(Number(current.threshold)) || Number(current.threshold) < 0)
      ) {
        found[branch.id] = t("products.form.errors.threshold");
      }
    });

    return found;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBanner("");

    const clientErrors = validate();
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      return;
    }

    setErrors({});
    setSaving(true);

    const submitted = branches
      .filter((branch) => row(branch.id).offered)
      .map((branch) => {
        const current = row(branch.id);
        return {
          branchId: branch.id,
          price: current.price,
          isNew: !current.assigned,
          openingStock: tracksStock ? current.openingStock : "0",
          threshold: tracksStock ? current.threshold : undefined,
        };
      });

    try {
      await syncProductBranches(product.id, submitted);
      onSaved();
    } catch (err) {
      // `branches.{index}.field` points at a submitted row; `removed.{branchId}`
      // at a branch that was unticked. Both land on that branch's row.
      const byBranch = {};
      Object.entries(apiFieldErrors(err)).forEach(([key, messages]) => {
        const indexed = key.match(/^branches\.(\d+)\./);
        const removed = key.match(/^removed\.(.+)$/);
        const branchId = indexed ? submitted[Number(indexed[1])]?.branchId : removed?.[1];
        if (branchId && !byBranch[branchId]) byBranch[branchId] = messages?.[0];
      });
      setErrors(byBranch);
      setBanner(apiErrorMessage(err, t("products.branches.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  if (!open || !product) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
      <div role="dialog" aria-modal="true" className="w-full max-w-2xl rounded-xl bg-surface shadow-xl">
        <form onSubmit={submit} className="flex max-h-[85vh] flex-col" noValidate>
          <div className="flex items-center justify-between border-b border-line px-5 py-4">
            <div>
              <h2 className="text-lg font-semibold">{t("products.branches.title")}</h2>
              <p className="text-xs text-ink-2">{product.name}</p>
            </div>
            <button type="button" onClick={onClose} className="text-sm text-ink-2 hover:text-ink">
              {t("common.close")}
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4">
            {banner && (
              <div className="mb-3 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
                {banner}
              </div>
            )}

            {loading ? (
              <p className="py-6 text-center text-sm text-ink-2">{t("common.loading")}</p>
            ) : branches.length === 0 ? (
              <p className="py-6 text-center text-sm text-ink-2">{t("products.branches.noBranches")}</p>
            ) : (
              <ul className="divide-y divide-line">
                {branches.map((branch) => {
                  const current = row(branch.id);
                  const priceId = `bp-price-${branch.id}`;
                  const stockId = `bp-stock-${branch.id}`;
                  const thresholdId = `bp-threshold-${branch.id}`;

                  return (
                    <li key={branch.id} className="py-3">
                      <div className="flex flex-wrap items-end gap-3">
                        <label className="flex min-w-[10rem] flex-1 items-center gap-2 self-center text-sm">
                          <input
                            type="checkbox"
                            checked={current.offered}
                            onChange={(e) => toggle(branch.id, e.target.checked)}
                            className="h-4 w-4 rounded border-line-strong text-accent-text"
                          />
                          <span>
                            {branch.name}
                            <span className="ms-2 text-xs text-ink-2" dir="ltr">
                              {branch.currency?.code}
                            </span>
                          </span>
                        </label>

                        <div>
                          <label className="field-label text-xs" htmlFor={priceId}>
                            {t("products.branches.price")}
                          </label>
                          <input
                            id={priceId}
                            type="number"
                            step="0.01"
                            min="0"
                            dir="ltr"
                            disabled={!current.offered}
                            className="field-input w-28 disabled:bg-raised"
                            value={current.price}
                            onChange={(e) => update(branch.id, { price: e.target.value })}
                          />
                        </div>

                        {tracksStock && (
                          <div>
                            <label className="field-label text-xs" htmlFor={stockId}>
                              {current.assigned
                                ? t("products.branches.stock")
                                : t("products.branches.openingStock")}
                            </label>
                            {current.assigned ? (
                              <div className="flex h-[38px] items-center gap-2">
                                <span id={stockId} className="w-12 font-medium" dir="ltr">
                                  {current.stock}
                                </span>
                                <Link
                                  to={`/branches/${branch.id}/products?adjust=${product.id}`}
                                  className="text-xs font-semibold text-accent-text hover:underline"
                                >
                                  {t("products.adjustStock")}
                                </Link>
                              </div>
                            ) : (
                              <input
                                id={stockId}
                                type="number"
                                step="1"
                                min="0"
                                dir="ltr"
                                disabled={!current.offered}
                                className="field-input w-24 disabled:bg-raised"
                                value={current.openingStock}
                                onChange={(e) => update(branch.id, { openingStock: e.target.value })}
                              />
                            )}
                          </div>
                        )}

                        {tracksStock && (
                          <div>
                            <label
                              className="field-label text-xs"
                              htmlFor={thresholdId}
                              title={t("products.branches.thresholdHint")}
                            >
                              {t("products.branches.threshold")}
                            </label>
                            <input
                              id={thresholdId}
                              type="number"
                              step="1"
                              min="0"
                              dir="ltr"
                              disabled={!current.offered}
                              placeholder={
                                defaultThreshold != null
                                  ? t("products.branches.thresholdDefault", { count: defaultThreshold })
                                  : t("products.form.thresholdPlaceholder")
                              }
                              aria-describedby={`${thresholdId}-hint`}
                              className="field-input w-28 disabled:bg-raised"
                              value={current.threshold}
                              onChange={(e) => update(branch.id, { threshold: e.target.value })}
                            />
                            <span id={`${thresholdId}-hint`} className="sr-only">
                              {t("products.branches.thresholdHint")}
                            </span>
                          </div>
                        )}
                      </div>

                      {errors[branch.id] && <p className="field-error mt-1">{errors[branch.id]}</p>}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-4">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn-primary" disabled={saving || loading}>
              {saving ? t("common.loading") : t("common.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
